import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { ChevronRight, ExpandMore } from '@mui/icons-material';
import {
  Card,
  CardContent,
  Chip,
  ListItem,
  Stack,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import React from 'react';

interface ExpandableCardProps<T> {
  variant?: 'rules' | 'actions';
  title: string;
  subtitle?: string;
  items: T[];
  isExpanded: boolean;
  onToggle: (key: string) => void;
  toggleKey: string;
  chipLabel: string;
  chipSx?: SxProps<Theme>;
  renderExpandedContent: (items: T[]) => React.ReactNode;
}

export const ExpandableCard = <T,>({
  variant = 'rules',
  title,
  subtitle,
  items,
  isExpanded,
  onToggle,
  toggleKey,
  chipLabel,
  chipSx,
  renderExpandedContent,
}: ExpandableCardProps<T>) => {
  const { tokens } = useTheme<LensTheme>();

  return (
    <Card
      sx={{
        padding: tokens.core.spacing[3].value,
        border: `1px solid ${tokens.semantic.color.action.secondary.disabledOutline.value}`,
        borderRadius: tokens.semantic.radius.lg.value,
        boxShadow: 'none !important',
      }}
    >
      <CardContent
        sx={{
          padding: '0 !important',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: tokens.core.spacing[2].value,
          width: '100%',
        }}
      >
        <ListItem
          sx={{
            padding: '0 !important',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: tokens.core.spacing['1_5'].value,
            width: '100%',
            boxShadow: 'none',
          }}
        >
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              gap: tokens.core.spacing[2].value,
            }}
          >
            {isExpanded ? (
              <ExpandMore
                onClick={() => onToggle(toggleKey)}
                sx={{
                  color:
                    tokens.semantic.color.action.secondary.onSecondary.value,
                  cursor: 'pointer',
                }}
              />
            ) : (
              <ChevronRight
                onClick={() => onToggle(toggleKey)}
                sx={{
                  color:
                    tokens.semantic.color.action.secondary.onSecondary.value,
                  cursor: 'pointer',
                }}
              />
            )}
            {variant === 'rules' ? (
              <Typography
                variant="body1"
                sx={{
                  fontWeight: tokens.semantic.fontWeight.emphasis.value,
                }}
              >
                {title}
              </Typography>
            ) : (
              <Stack
                direction="column"
                sx={{
                  gap: tokens.core.spacing['0_5'].value,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: tokens.semantic.fontWeight.emphasis.value,
                  }}
                >
                  {title}
                </Typography>
                {subtitle && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: tokens.semantic.color.type.muted.value,
                    }}
                  >
                    {subtitle}
                  </Typography>
                )}
              </Stack>
            )}
          </Stack>
          <Chip
            label={chipLabel}
            variant="outlined"
            size="small"
            sx={{
              padding: `0 ${tokens.core.spacing['0_25'].value}`,
              borderRadius: tokens.semantic.radius.full.value,
              border: `1px solid ${tokens.semantic.color.action.secondary.outline.value}`,
              '& .MuiChip-label': {
                fontFamily: tokens.semantic.font.text.body.fontFamily.value,
                fontSize: `${tokens.semantic.font.text.body.fontSize.value}px`,
                letterSpacing: `${tokens.semantic.font.text.body.letterSpacing.value}px`,
                lineHeight: `${tokens.semantic.font.text.body.lineHeight.value}px`,
                fontStyle: 'normal',
              },
              ...chipSx,
            }}
          />
        </ListItem>

        {isExpanded && renderExpandedContent(items)}
      </CardContent>
    </Card>
  );
};
